import {DateTime} from 'luxon';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useConstant} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {FrontEvent, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {futureTimeToDate} from '../../../helpers/modelHelpers';
import {FutureTimeValuesEnum} from '../../../models/api/futureTimeValueModel';
import {DateTimePicker} from '../dateTime/dateTimePicker';

/*
 * Types.
 */

export interface FutureTimeDropdownCustomPublicProps {
  selectButton: ReactNode;
  clearButton?: ReactNode;
  updateButton?: ReactNode;
  customContent?: ReactNode;
  value?: number;
}

interface FutureTimeDropdownCustomProps extends FutureTimeDropdownCustomPublicProps {
  hideDateTimePicker: HandlerOf<FrontEvent>;
  onValueSelect?: HandlerOf<number>;
  onValueClear?: Handler;
  onRequestClose?: Handler;
  timeFormat: TimeFormatsEnum;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  width: 250px;
`;

const StyledContentDiv = styled.div`
  padding: 4px 12px 12px 12px;
`;

const StyledButtonsDiv = styled.div`
  padding: 4px 12px 12px 12px;
`;

/*
 * Component.
 */

export const FutureTimeDropdownCustom: FC<FutureTimeDropdownCustomProps> = (props) => {
  const {value, customContent, onValueSelect, onValueClear, onRequestClose} = props;
  const [selectedDateTime, setSelectedDateTime] = useState(() => parseDateTime(value));
  const highlightedDateTime = useConstant(() => DateTime.local());

  /*
   * Event handlers.
   */

  const onValueSelectClick = useCallback(() => {
    if (!selectedDateTime || !onValueSelect) {
      return;
    }

    onValueSelect(selectedDateTime.valueOf());
  }, [onValueSelect, selectedDateTime]);

  /*
   * Render.
   */

  const {selectButton, updateButton, clearButton, timeFormat} = props;
  const renderButtons = () => {
    // If there is no existing value, offer to set it.
    if (!value) {
      return (
        <ButtonGroup spacingSize={VisualSizesEnum.SMALL}>
          <ButtonGroupSpacer />
          <Button size={VisualSizesEnum.SMALL} onClick={onRequestClose}>
            <Trans id="dropdown-cancel">Cancel</Trans>
          </Button>
          <Button
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!selectedDateTime}
            onClick={onValueSelectClick}
          >
            {selectButton}
          </Button>
        </ButtonGroup>
      );
    }

    // Otherwise, allow clearing or updating.
    return (
      <ButtonGroup spacingSize={VisualSizesEnum.SMALL}>
        <Button size={VisualSizesEnum.SMALL} onClick={onValueClear}>
          {clearButton}
        </Button>
        <ButtonGroupSpacer />
        <Button size={VisualSizesEnum.SMALL} onClick={onRequestClose}>
          <Trans id="dropdown-cancel">Cancel</Trans>
        </Button>
        <Button
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={!selectedDateTime}
          onClick={onValueSelectClick}
        >
          {updateButton}
        </Button>
      </ButtonGroup>
    );
  };

  // We use 9 AM as a sane default time for a custom snooze. It may be valuable to the teammate's custom
  // "beginning of day" time i.e. when their Do Not Disturb Schedule ends (PB-28726)
  const defaultTime = DateTime.fromMillis(futureTimeToDate(FutureTimeValuesEnum.TOMORROW_9AM));

  return (
    <StyledContainerDiv>
      <DateTimePicker
        defaultTime={defaultTime}
        selectedDateTime={selectedDateTime}
        highlightedDateTime={highlightedDateTime}
        minDateTime={DateTime.local()}
        onDateTimeSelect={setSelectedDateTime}
        onDateTimeConfirm={onValueSelectClick}
        timeFormat={timeFormat}
      />
      {customContent && <StyledContentDiv>{customContent}</StyledContentDiv>}
      <StyledButtonsDiv>{renderButtons()}</StyledButtonsDiv>
    </StyledContainerDiv>
  );
};

/*
 * Helpers.
 */

function parseDateTime(value: number | undefined) {
  return isInteger(value) ? DateTime.fromMillis(value) : undefined;
}
